import React from 'react';
import { Input, Select, DatePicker, Checkbox, Radio } from 'antd';
import { CustomFieldOption, TicketField } from '@/types';

interface FieldRendererProps {
  field: TicketField;
  value?: any;
  onChange?: (val: any) => void;
  disabled?: boolean;
}

const inputClassName =
  'border border-solid custom-form-placeholder-input relative border-customGrayShade4 placeholder:text-GrayShade4 placeholder:font-[400] placeholder:text-[16px] placeholder:leading-[20.48px] h-[48px] rounded-lg';

const selectClassName =
  'border border-solid custom-select-item placeholder:capitalize custom-select-placeholder h-[52px] rounded-[10px] border-customGrayShade4';

const textareaClassName = 'rounded-lg border border-solid border-customGrayShade4';

const renderSelectOptions = (options?: CustomFieldOption[]) =>
  options?.map((opt) => (
    <Select.Option key={opt.value} value={opt.value}>
      {opt.name}
    </Select.Option>
  ));

const resolveTextInputType = (inputKey?: string): 'email' | 'tel' | 'url' | 'hidden' | 'text' => {
  const normalizedTitle = inputKey?.toLowerCase() ?? '';
  if (normalizedTitle.includes('email')) return 'email';
  if (normalizedTitle.includes('phone')) return 'tel';
  if (normalizedTitle.includes('url')) return 'url';
  if (normalizedTitle.includes('hidden')) return 'hidden';
  return 'text';
};

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  onChange,
  disabled,
}) => {
  switch (field.type) {
    case 'text': {
      const inputType = resolveTextInputType(field.key);
      if (inputType === 'hidden') return null;
      return (
        <Input
          type={inputType}
          placeholder={field.description}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          variant='borderless'
          className={inputClassName}
        />
      );
    }

    case 'decimal':
      return (
        <Input
          type='number'
          placeholder={field.description}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          variant='borderless'
          className={inputClassName}
        />
      );

    case 'textarea':
      return (
        <Input.TextArea
          rows={4}
          placeholder={field.description}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          variant='borderless'
          className={textareaClassName}
        />
      );

    case 'number':
      return (
        <Input
          type='number'
          placeholder={field.description}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          variant='borderless'
          className={inputClassName}
        />
      );

    case 'email':
      return (
        <Input
          type='email'
          placeholder={field.description}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          variant='borderless'
          className={inputClassName}
        />
      );

    case 'phone':
      return (
        <Input
          type='tel'
          placeholder={field.description}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          variant='borderless'
          className={inputClassName}
        />
      );

    case 'url':
      return (
        <Input
          type='url'
          placeholder={field.description}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          variant='borderless'
          className={inputClassName}
        />
      );

    case 'date':
      return (
        <DatePicker
          style={{ width: '100%' }}
          value={value}
          onChange={onChange}
          disabled={disabled}
          variant='borderless'
          className={inputClassName}
        />
      );

    case 'dropdown':
    case 'tagger':
    case 'select':
      return (
        <Select
          placeholder={field.description}
          value={value}
          onChange={onChange}
          disabled={disabled}
          variant='borderless'
          style={{ width: '100%' }}
          className={selectClassName}
        >
          {renderSelectOptions(field.custom_field_options)}
        </Select>
      );

    case 'multiselect':
      return (
        <Select
          mode='multiple'
          placeholder={field.description}
          value={value}
          onChange={onChange}
          disabled={disabled}
          variant='borderless'
          style={{ width: '100%' }}
          className={selectClassName}
        >
          {renderSelectOptions([])}
        </Select>
      );

    case 'radiobutton':
      return (
        <Radio.Group value={value} onChange={(e) => onChange?.(e.target.value)} disabled={disabled}>
          {'custom_field_options' in field &&
            field.custom_field_options?.map((option) => (
              <Radio key={option.id} value={option.value}>
                {option.name}
              </Radio>
            ))}
        </Radio.Group>
      );

    case 'checkbox':
      return (
        <Checkbox
          checked={value}
          onChange={(e) => onChange?.(e.target.checked)}
          disabled={disabled}
        />
      );

    default:
      console.warn(
        `FieldRenderer: Unsupported field type "${field.type}" for field "${field.title}"`,
      );
      return null;
  }
};
